/**
 * ParentalControlService -- Manages child profile restrictions.
 *
 * Loads parental controls from server, caches in IndexedDB for offline enforcement.
 * Tracks daily usage time, enforces allowed hours, blocked modules/games,
 * and provides PIN verification for accessing parental settings.
 *
 * Emits events: 'time-warning', 'time-expired', 'module-blocked'
 */

// ---------- Types ----------

export interface ParentalControls {
  enabled: boolean;
  blocked_modules: string[];
  blocked_game_keys: string[];
  allowed_hours_start: number; // 0-23
  allowed_hours_end: number;   // 0-23
  daily_time_limit_min: number | null; // null = unlimited
  difficulty_cap: 'easy' | 'medium' | 'hard' | 'expert' | null;
  require_approval_for: string[];
  pin_hash: string | null;
  paused: boolean;
}

export interface ChildActivity {
  screenTimeToday: number;     // minutes
  screenTimeWeek: number;
  gamesPlayed: { key: string; title: string; count: number }[];
  cardsReviewed: number;
  articlesRead: number;
  lastActiveTime: string | null;
}

export type ParentalEvent = 'time-warning' | 'time-expired' | 'module-blocked';

type ParentalEventCallback = (event: ParentalEvent, detail?: Record<string, unknown>) => void;

// ---------- Constants ----------

const LS_USAGE_KEY = 'aweni_usage_today';
const LS_USAGE_DATE_KEY = 'aweni_usage_date';
const LS_PIN_ATTEMPTS_KEY = 'aweni_pin_attempts';
const LS_PIN_LOCKOUT_KEY = 'aweni_pin_lockout';
const IDB_NAME = 'aweni_parental';
const IDB_STORE = 'controls';
const IDB_VERSION = 1;
const WARNING_MINUTES = 5;
const MAX_PIN_ATTEMPTS = 3;
const PIN_LOCKOUT_MS = 5 * 60 * 1000; // 5 minutes

// ---------- Default Controls ----------

function getDefaultControls(): ParentalControls {
  return {
    enabled: false,
    blocked_modules: [],
    blocked_game_keys: [],
    allowed_hours_start: 0,
    allowed_hours_end: 23,
    daily_time_limit_min: null,
    difficulty_cap: null,
    require_approval_for: [],
    pin_hash: null,
    paused: false,
  };
}

// ---------- IndexedDB Helpers ----------

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, IDB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbGet<T>(key: string): Promise<T | undefined> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readonly');
      const store = tx.objectStore(IDB_STORE);
      const req = store.get(key);
      req.onsuccess = () => resolve(req.result as T | undefined);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return undefined;
  }
}

async function idbSet(key: string, value: unknown): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      const store = tx.objectStore(IDB_STORE);
      const req = store.put(value, key);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch {
    // IndexedDB unavailable -- fall back to in-memory only
  }
}

// ---------- Service ----------

export class ParentalControlService {
  private _controls: ParentalControls;
  private _listeners: ParentalEventCallback[] = [];
  private _usageMinutes = 0;
  private _trackingInterval: ReturnType<typeof setInterval> | null = null;
  private _warningEmitted = false;
  private _expiredEmitted = false;
  private _apiBase: string;
  private _getToken: () => string | null;

  constructor(apiBase: string, getToken: () => string | null) {
    this._controls = getDefaultControls();
    this._apiBase = apiBase;
    this._getToken = getToken;
    this._loadUsageFromStorage();
  }

  // ---------- Initialization ----------

  /**
   * Load controls from server, fall back to IndexedDB cache.
   */
  async init(): Promise<void> {
    // Try server first
    const serverControls = await this._fetchFromServer();
    if (serverControls) {
      this._controls = serverControls;
      await idbSet('current', serverControls);
    } else {
      // Offline: use cached
      const cached = await idbGet<ParentalControls>('current');
      if (cached) {
        this._controls = cached;
      }
    }

    // Start time tracking if enabled and has a limit
    if (this._controls.enabled && this._controls.daily_time_limit_min !== null) {
      this._startTracking();
    }
  }

  // ---------- Check Methods ----------

  get isEnabled(): boolean {
    return this._controls.enabled && !this._controls.paused;
  }

  get controls(): Readonly<ParentalControls> {
    return this._controls;
  }

  canAccessModule(moduleId: string): boolean {
    if (!this.isEnabled) return true;
    return !this._controls.blocked_modules.includes(moduleId);
  }

  canPlayGame(gameKey: string): boolean {
    if (!this.isEnabled) return true;
    return !this._controls.blocked_game_keys.includes(gameKey);
  }

  isWithinAllowedHours(): boolean {
    if (!this.isEnabled) return true;

    const now = new Date();
    const hour = now.getHours();
    const { allowed_hours_start: start, allowed_hours_end: end } = this._controls;

    // Handle overnight ranges (e.g., start=8, end=21 is daytime; start=21, end=8 is nighttime)
    if (start <= end) {
      return hour >= start && hour <= end;
    } else {
      // Overnight: allowed if NOT in the blocked window
      return hour >= start || hour <= end;
    }
  }

  getRemainingTime(): number | null {
    if (!this.isEnabled || this._controls.daily_time_limit_min === null) return null;
    const remaining = this._controls.daily_time_limit_min - this._usageMinutes;
    return Math.max(0, remaining);
  }

  isTimeLimitReached(): boolean {
    const remaining = this.getRemainingTime();
    return remaining !== null && remaining <= 0;
  }

  requiresApproval(action: string): boolean {
    if (!this.isEnabled) return false;
    return this._controls.require_approval_for.includes(action);
  }

  getDifficultyCap(): string | null {
    if (!this.isEnabled) return null;
    return this._controls.difficulty_cap;
  }

  // ---------- PIN Verification ----------

  verifyPin(pin: string): boolean {
    if (this._isPinLocked()) return false;

    // Simple comparison -- server stores bcrypt hash, but for client-side
    // we do a basic check. Real verification should go through the server.
    // For offline mode, we store a simple hash locally.
    const stored = this._controls.pin_hash;
    if (!stored) return true; // No PIN set

    const match = this._simpleHash(pin) === stored || pin === stored;

    if (!match) {
      this._recordFailedAttempt();
      return false;
    }

    // Reset attempts on success
    this._resetPinAttempts();
    return true;
  }

  isPinLocked(): boolean {
    return this._isPinLocked();
  }

  getPinLockRemainingSeconds(): number {
    try {
      const lockUntil = Number(localStorage.getItem(LS_PIN_LOCKOUT_KEY) || '0');
      if (!lockUntil) return 0;
      const remaining = Math.ceil((lockUntil - Date.now()) / 1000);
      return Math.max(0, remaining);
    } catch {
      return 0;
    }
  }

  // ---------- Time Tracking ----------

  getUsageMinutesToday(): number {
    return this._usageMinutes;
  }

  /**
   * Record an activity event (games, cards, articles) for the parental dashboard.
   */
  async recordActivity(type: 'game_played' | 'card_reviewed' | 'article_read', meta?: Record<string, string>): Promise<void> {
    const token = this._getToken();
    if (!token) return;

    try {
      await fetch(`${this._apiBase}/preferences`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          key: 'child_activity',
          value: { type, meta, timestamp: new Date().toISOString() },
        }),
      });
    } catch {
      // Non-critical -- activity tracking is best-effort
    }
  }

  // ---------- Event System ----------

  on(cb: ParentalEventCallback): () => void {
    this._listeners.push(cb);
    return () => {
      this._listeners = this._listeners.filter((l) => l !== cb);
    };
  }

  private _emit(event: ParentalEvent, detail?: Record<string, unknown>): void {
    for (const cb of this._listeners) {
      try { cb(event, detail); } catch { /* listener error -- skip */ }
    }
  }

  // ---------- Server Communication ----------

  private async _fetchFromServer(): Promise<ParentalControls | null> {
    const token = this._getToken();
    if (!token) return null;

    try {
      const res = await fetch(`${this._apiBase}/preferences?key=parental_controls`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
      });

      if (!res.ok) return null;

      const data = await res.json();
      if (data && data.value) {
        return { ...getDefaultControls(), ...data.value };
      }
      return null;
    } catch {
      return null;
    }
  }

  async saveControls(controls: Partial<ParentalControls>): Promise<boolean> {
    const updated = { ...this._controls, ...controls };
    this._controls = updated;
    await idbSet('current', updated);

    const token = this._getToken();
    if (!token) return false;

    try {
      const res = await fetch(`${this._apiBase}/preferences`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ key: 'parental_controls', value: updated }),
      });

      return res.ok;
    } catch {
      return false;
    }
  }

  // ---------- Usage Tracking Internals ----------

  private _loadUsageFromStorage(): void {
    try {
      const savedDate = localStorage.getItem(LS_USAGE_DATE_KEY);
      const today = new Date().toDateString();

      if (savedDate === today) {
        this._usageMinutes = Number(localStorage.getItem(LS_USAGE_KEY) || '0');
      } else {
        // New day -- reset
        localStorage.setItem(LS_USAGE_DATE_KEY, today);
        localStorage.setItem(LS_USAGE_KEY, '0');
        this._usageMinutes = 0;
      }
    } catch {
      this._usageMinutes = 0;
    }
  }

  private _startTracking(): void {
    if (this._trackingInterval) return;

    // Tick every 60 seconds
    this._trackingInterval = setInterval(() => {
      this._usageMinutes += 1;

      try {
        localStorage.setItem(LS_USAGE_KEY, String(this._usageMinutes));
      } catch { /* quota -- ignore */ }

      const remaining = this.getRemainingTime();
      if (remaining === null) return;

      // 5-minute warning
      if (remaining <= WARNING_MINUTES && remaining > 0 && !this._warningEmitted) {
        this._warningEmitted = true;
        this._emit('time-warning', { remainingMinutes: remaining });
      }

      // Time expired
      if (remaining <= 0 && !this._expiredEmitted) {
        this._expiredEmitted = true;
        this._emit('time-expired');
      }
    }, 60_000);
  }

  stopTracking(): void {
    if (this._trackingInterval) {
      clearInterval(this._trackingInterval);
      this._trackingInterval = null;
    }
  }

  // ---------- PIN Helpers ----------

  private _simpleHash(pin: string): string {
    // Simple client-side hash for offline comparison.
    // Not cryptographically secure -- real validation is server-side.
    let hash = 0;
    for (let i = 0; i < pin.length; i++) {
      const char = pin.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return 'pin_' + Math.abs(hash).toString(36);
  }

  private _isPinLocked(): boolean {
    try {
      const lockUntil = Number(localStorage.getItem(LS_PIN_LOCKOUT_KEY) || '0');
      return lockUntil > Date.now();
    } catch {
      return false;
    }
  }

  private _recordFailedAttempt(): void {
    try {
      const attempts = Number(localStorage.getItem(LS_PIN_ATTEMPTS_KEY) || '0') + 1;
      localStorage.setItem(LS_PIN_ATTEMPTS_KEY, String(attempts));

      if (attempts >= MAX_PIN_ATTEMPTS) {
        localStorage.setItem(LS_PIN_LOCKOUT_KEY, String(Date.now() + PIN_LOCKOUT_MS));
        localStorage.setItem(LS_PIN_ATTEMPTS_KEY, '0');
      }
    } catch { /* storage unavailable */ }
  }

  private _resetPinAttempts(): void {
    try {
      localStorage.removeItem(LS_PIN_ATTEMPTS_KEY);
      localStorage.removeItem(LS_PIN_LOCKOUT_KEY);
    } catch { /* ignore */ }
  }

  // ---------- Cleanup ----------

  destroy(): void {
    this.stopTracking();
    this._listeners = [];
  }
}

// ---------- Singleton ----------

let _instance: ParentalControlService | null = null;

export function getParentalControlService(): ParentalControlService | null {
  return _instance;
}

export function initParentalControlService(
  apiBase: string,
  getToken: () => string | null,
): ParentalControlService {
  if (_instance) _instance.destroy();
  _instance = new ParentalControlService(apiBase, getToken);
  return _instance;
}
